import dynamic from "next/dynamic";
import Link from "next/link";
import { KeyedMutator } from "swr";

import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import classNames from "classnames";
import format from "date-fns/format";
import parseJSON from "date-fns/parseJSON";
import unescape from "lodash/unescape";
import type { Insight } from "models/insight";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faUnlock } from "@fortawesome/free-solid-svg-icons/faUnlock";

import AddToWatchlistButton from "components/AddToWatchlistButton";
import AlertBox from "components/AlertBox";
import { Title } from "components/customElements/title";
import DianomiAd from "components/DianomiAd";
import ErrorBoundary from "components/ErrorBoundary";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import InsightDetail from "components/InsightLongForm/InsightDetail";
import TopDownTicker from "components/InsightLongForm/TopDownTicker";
import RemovePaywallButton from "components/RemovePaywallButton";
import RepublishButton from "components/RepublishButton";
import RichContent from "components/RichContent";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useEffectOnce } from "hooks/useEffectOnce";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";
import { getImperativeColor } from "utils/insight";

import Badge from "./Badge";
import InsightBoomerang from "./InsightBoomerang";
import InsightSmartTakes from "./InsightSmartTakes";
import InsightsPostHighlightedCard from "./InsightsPostHighlightedCard";
import style from "./style.module.scss";

const ENABLE_SMART_TAKES = true;

const ProfileCard = dynamic(
  () => import("components/InsightLongForm/ProfileCard")
);
const InsightSummary = dynamic(
  () => import("components/InsightLongForm/InsightSummary"),
  {
    ssr: false,
  }
);
const InsightLikeSection = dynamic(
  () => import("components/InsightLongForm/InsightLikeSection"),
  {
    ssr: false,
  }
);
const InsightRelatedContentV2 = dynamic(
  () => import("components/InsightLongForm/InsightRelatedContentV2"),
  {
    ssr: false,
  }
);
const InsightDisclosure = dynamic(
  () => import("components/InsightLongForm/InsightDisclosure"),
  {
    ssr: false,
  }
);
const InsightDiscussionsList = dynamic(
  () => import("components/InsightLongForm/InsightDiscussionsList"),
  {
    ssr: false,
  }
);
const SignUpForm = dynamic(() => import("components/SignUpForm"), {
  ssr: true,
});
const InsightDocuments = dynamic(() => import("components/InsightDocuments"));
const InsightStreamCard = dynamic(() => import("components/InsightStreamCard"));
const InsightRelatedEntities = dynamic(
  () => import("components/InsightRelatedEntities"),
  {
    ssr: false,
  }
);
const AccountUpgradeForm = dynamic(
  () => import("components/AccountUpgradeForm"),
  {
    ssr: false,
  }
);
const InsightRestrictionState = dynamic(
  () => import("components/InsightRestrictionState"),
  {
    ssr: false,
  }
);
const DiscussionRestrictionState = dynamic(
  () => import("components/DiscussionRestrictionState"),
  {
    ssr: false,
  }
);
const GoogleTranslationToogle = dynamic(
  () => import("components/GoogleTranslationToogle"),
  {
    ssr: false,
  }
);
const PressPassNote = dynamic(() => import("./PressPassNote"), {
  ssr: false,
});

const TranslationNav = dynamic(() => import("components/TranslationNav"), {
  ssr: false,
});

type InsightLongFormProps = {
  insight: Insight;
  mutate?: KeyedMutator<any>;
};

const InsightLongForm = ({ insight, mutate }: InsightLongFormProps) => {
  const {
    id,
    slug,
    tagline,
    byline,
    boomerangReason,

    imperative,
    conviction,
    totalCombinedRead,
    commentsCount,

    isPublic,
    isCommissioned,
    isBoost,
    isExchangeSponsored,

    fromSearchBot,

    publicationType,

    publishedAt,
    republishedAt,

    account,
    author,
    primaryEntity,
    contentVertical,

    restrictionReason,

    timeToRead,

    smartSummary,
  } = insight;

  const { authenticated, account: loggedInAccount } = useAuthentication();
  const { toggleModal } = useSignInSignUp();
  const media = useMediaQuery();
  const { t } = useTranslation();

  const { tableOfContents, relatedThemes } = smartSummary || {};

  const shouldShowSmartTakes =
    !!relatedThemes && loggedInAccount?.canViewSmartSummary;

  const summaryRef = useRef<HTMLDivElement>(null);
  const summaryIntersection = useIntersectionObserver(summaryRef, {
    freezeOnceVisible: true,
  });

  const insightDetailRef = useRef<HTMLElement>(null);
  const insightDetailIntersection = useIntersectionObserver(insightDetailRef, {
    freezeOnceVisible: true,
  });

  const discussionsRef = useRef<HTMLElement>(null);
  const discussionsIntersection = useIntersectionObserver(discussionsRef, {
    freezeOnceVisible: true,
  });

  const attachmentRef = useRef<HTMLDivElement>(null);
  const attachmentIntersection = useIntersectionObserver(attachmentRef, {
    freezeOnceVisible: true,
  });

  const insightStreamRef = useRef<HTMLElement>(null);
  const insightStreamIntersection = useIntersectionObserver(insightStreamRef, {
    freezeOnceVisible: true,
  });

  const relatedEntitiesRef = useRef<HTMLDivElement>(null);
  const relatedEntitiesIntersection = useIntersectionObserver(
    relatedEntitiesRef,
    {
      freezeOnceVisible: true,
    }
  );

  const [expandSmartTakes, setExpandSmartTakes] = useState(
    authenticated && loggedInAccount?.isPreviewPass
  );
  const [expandDetail, setExpandDetail] = useState(false);
  const [expandDiscussions, setExpandDiscussions] = useState(authenticated);

  const shouldShowSignup = useMemo(() => {
    if (insight?.isPrimer && !authenticated) {
      return true;
    }

    return !authenticated && !isPublic && !fromSearchBot;
  }, [authenticated, fromSearchBot, insight?.isPrimer, isPublic]);

  const shouldShowByline = useMemo(() => {
    if (insight?.isPrimer && !authenticated) {
      return true;
    }

    return !authenticated && !isPublic;
  }, [authenticated, insight?.isPrimer, isPublic]);

  const shouldShowToC = useMemo(() => {
    if (
      insight?.isPrimer &&
      ENABLE_SMART_TAKES &&
      !authenticated &&
      !!tableOfContents?.length
    ) {
      return true;
    }

    return (
      ENABLE_SMART_TAKES &&
      !authenticated &&
      !!tableOfContents?.length &&
      !isPublic
    );
  }, [authenticated, insight?.isPrimer, isPublic, tableOfContents?.length]);

  const canViewDetail = useMemo(() => {
    if (insight?.isPrimer && !authenticated) {
      return false;
    }

    return authenticated || isPublic || fromSearchBot;
  }, [authenticated, fromSearchBot, insight?.isPrimer, isPublic]);

  const imperativeColor = getImperativeColor(imperative);
  const isHighlightedPost = conviction === 1;
  const isBoomerangInsight = !!republishedAt;
  const views = numeral(totalCombinedRead).format("0.[0]a");

  const badges = useMemo(() => {
    const badgesData = [];
    // get tags details to an array if the condition is true
    isCommissioned &&
      badgesData.push({
        text: "Smartkarma Originals",
        color: "#1c896e",
        toolTipText: "Exclusive research engaged by Smartkarma. ",
        tooltipLink: `${emberAppBaseUrl}/home/share-your-research-ideas/`,
        toolTipLinkText: "Send us your research ideas",
      });
    isBoost &&
      badgesData.push({
        text: "Boost Research",
        color: "#0276be",
        toolTipText:
          "Exclusive research from participants in the Boost programme",
        tooltipLink: "",
        toolTipLinkText: "",
      });
    isExchangeSponsored &&
      badgesData.push({
        text: "Exchange Sponsored",
        color: "#9fc571",
        toolTipText: "Independent Research Funded by Exchanges",
        tooltipLink: "",
        toolTipLinkText: "",
      });

    // map the array and display tags
    return badgesData.map((item: any, key) => {
      return <Badge item={item} key={key} />;
    });
  }, [isBoost, isCommissioned, isExchangeSponsored]);

  const widgets = useMemo(() => {
    return (
      <>
        {media.handheld && (
          <section className={style.insightSection} ref={insightStreamRef}>
            <Title>{t("INSIGHT STREAM")}</Title>

            {insightStreamIntersection?.isIntersecting && (
              <Suspense fallback={`Loading...`}>
                <InsightStreamCard
                  insight={insight}
                  hideTitle={true}
                  className={style.insightStream}
                  hideForkCta
                />
              </Suspense>
            )}
          </section>
        )}

        {media.handheld && (
          <section className={style.insightSection} ref={relatedEntitiesRef}>
            {relatedEntitiesIntersection?.isIntersecting && (
              <Suspense fallback={`Loading...`}>
                <InsightRelatedEntities
                  slug={insight.slug}
                  customLayout={(content) => (
                    <>
                      <Title>{t("Related Entities")}</Title>
                      {content}
                    </>
                  )}
                />
              </Suspense>
            )}
          </section>
        )}
      </>
    );
  }, [
    insight,
    insightStreamIntersection?.isIntersecting,
    media.handheld,
    relatedEntitiesIntersection?.isIntersecting,
    t,
  ]);

  const { trackActivity } = useActivityTracker();
  useEffectOnce(
    (acknowledge) => {
      if (discussionsIntersection?.isIntersecting && insight) {
        trackActivity("comment.view_thread", insight, {
          page: window.location.href,
        });
        acknowledge();
      }
    },
    [discussionsIntersection, insight]
  );

  useEffectOnce(
    (acknowledge) => {
      if (loggedInAccount?.isPreviewPass) {
        setExpandSmartTakes(true);
        acknowledge();
      }
    },
    [loggedInAccount?.isPreviewPass]
  );

  useEffect(() => {
    setExpandSmartTakes(false);
  }, [insight.slug]);

  if (!insight) return null;

  return (
    <ErrorBoundary>
      <div className={style.insights_post_container}>
        <PressPassNote insight={insight} />
        <ErrorBoundary autoRerender>
          <div className={twMerge(style.small_heading_container, "flex-wrap")}>
            <span
              className="capitalize font-medium text-[15px] lg:text-[18px]"
              style={{ color: imperativeColor }}
            >
              {imperative}
            </span>
            <div className="flex-1 flex">
              {insight.isTopDown ? (
                <TopDownTicker insight={insight} />
              ) : (
                primaryEntity && (
                  <>
                    <Link
                      href={`/entities/${primaryEntity.slug}`}
                      passHref
                      prefetch={false}
                    >
                      <h3 className="font-normal">
                        {primaryEntity.prettyName}
                      </h3>
                    </Link>
                    <AddToWatchlistButton entity={primaryEntity}>
                      {({
                        addToWatchlist,
                        watchlists,
                        isWatching,
                        isLoading,
                      }) => (
                        <button
                          className={twMerge(
                            style.watchlist_btn,
                            isLoading && "opacity-50 cursor-not-allowed"
                          )}
                          disabled={isLoading}
                          onClick={addToWatchlist}
                        >
                          <FontAwesomeIcon
                            icon={isWatching ? faCheck : faPlus}
                            fixedWidth
                          />
                          <span className="hidden md:inline ml-[5px]">
                            {isWatching ? `${watchlists?.length} ` : ""}
                            Watchlist{watchlists?.length > 1 ? "s" : ""}
                          </span>
                        </button>
                      )}
                    </AddToWatchlistButton>
                  </>
                )
              )}
            </div>
            <div className="hidden md:flex flex-col md:flex-row space-y-[5px] md:space-y-0 md:space-x-[5px] ">
              <RepublishButton insight={insight} onUpdate={() => mutate?.()} />
              <RemovePaywallButton
                insight={insight}
                onUpdate={() => mutate?.()}
              />
            </div>
          </div>
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          <div className={style.tags_container}>{badges}</div>
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          <h1 className="break-words">
            {insight?.isEmbargoed && (
              <FontAwesomeIcon
                icon={faUnlock}
                className={"text-shady-gray mr-[5px]"}
              />
            )}
            {tagline}
          </h1>
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          {isHighlightedPost && (
            <InsightsPostHighlightedCard profile={author} />
          )}
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          <div className={style.statsContainer}>
            <div>
              <a
                href="#"
                onClick={() =>
                  window.open(
                    `${emberAppBaseUrl}/dashboard?collection=all-insights&filters=verticals:${contentVertical?.id}`
                  )
                }
              >
                {contentVertical?.name}
              </a>
            </div>
            <div className={style.counters} suppressHydrationWarning>
              <span>
                {views} View{totalCombinedRead > 1 ? "s" : ""}
              </span>
              {commentsCount ? (
                <span>
                  {numeral(commentsCount).format("0.[0]a")} Discussion
                  {commentsCount > 1 ? "s" : ""}
                </span>
              ) : (
                <></>
              )}
              {publishedAt ? (
                <span suppressHydrationWarning>
                  {format(parseJSON(publishedAt), "dd MMM yyyy HH:mm")}
                </span>
              ) : (
                <></>
              )}
            </div>
            {publicationType && <div>{publicationType}</div>}
          </div>
        </ErrorBoundary>

        {!loggedInAccount && <TranslationNav />}

        <ErrorBoundary autoRerender>
          {shouldShowByline && byline && (
            <RichContent className="bg-[#def2f2] italic p-[15px] font-light mt-[15px] text-subtle-black rounded-lg">
              {unescape(byline)}
            </RichContent>
          )}
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          {shouldShowToC && (
            <section className={style.insightSection}>
              <Title>{t("What is covered in the Full Insight")}:</Title>
              <div className="bg-nearly-white rounded-[7px] p-[15px]">
                <RichContent>
                  <ul className="!mb-0">
                    {tableOfContents?.map((content) => (
                      <li className="last-of-type:mb-0" key={content}>
                        {content}
                      </li>
                    ))}
                  </ul>
                </RichContent>
              </div>
            </section>
          )}
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          {authenticated && isBoomerangInsight && (
            <InsightBoomerang
              republishedAt={republishedAt}
              boomerangReason={boomerangReason}
              account={author}
            />
          )}
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          {(media.handheld || !authenticated) && author && (
            <div className={style.topPadding}>
              <ProfileCard insight={insight} profile={author} isCompactMode />
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          {shouldShowSmartTakes && (
            <section
              className={twMerge(
                style.insightSection,
                "bg-[rgba(2,118,190,.1)] rounded-lg !p-[10px] mt-[15px]"
              )}
            >
              <Title
                className={twMerge(
                  "flex cursor-pointer pb-[10px]",
                  !expandSmartTakes && "!border-none !mb-0 pb-0"
                )}
              >
                <div
                  className={twMerge("inline", "cursor-pointer")}
                  onClick={() => {
                    if (authenticated) {
                      trackActivity(
                        expandSmartTakes
                          ? "smart_takes.section_collapsed"
                          : "smart_takes.section_expanded"
                      );
                      setExpandSmartTakes(!expandSmartTakes);
                    } else {
                      trackActivity("smart_takes.signup_click");
                      toggleModal("sign-up");
                    }
                  }}
                >
                  <span className="mr-[5px]">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="https://assets.smartkarma.com/icons/sparkle.svg"
                      alt="Quiddity Northbound Monitor"
                      width={20}
                      height={22}
                      className="pb-[2px]"
                    />
                  </span>
                  SMART TAKES
                  <OverlayTrigger
                    trigger={["hover", "click"]}
                    overlay={
                      <Tooltip className="[&>.tooltip-inner]:max-w-[500px]">
                        <div>
                          Smart Takes are AI-generated summaries of premium
                          Insights on Smartkarma. These concise overviews are
                          crafted to save your time by distilling essential
                          information across four critical dimensions: Key
                          Takeaways, Bullish Points, Bearish Points, and
                          Catalysts to Watch. This feature enhances the
                          discovery and utilization of Insights on Smartkarma,
                          enabling more efficient time management for informed
                          decision-making.
                        </div>
                      </Tooltip>
                    }
                  >
                    <span onClick={(e) => e.stopPropagation()}>
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="ml-[5px] text-active-black"
                      />
                    </span>
                  </OverlayTrigger>
                </div>
                {shouldShowSignup && (
                  <span
                    className="ml-[5px] font-normal text-ink-pink hover:text-ink-pink normal-case cursor-pointer"
                    onClick={() => {
                      trackActivity("smart_takes.signup_click");
                      toggleModal("sign-up");
                    }}
                  >
                    {" "}
                    (<span>{t("Sign Up to Access")}</span>)
                  </span>
                )}

                {loggedInAccount?.isPreviewPass && (
                  <span className="ml-[5px] normal-case text-[14px]">
                    <FontAwesomeIcon icon={faUnlock} /> Unlocked
                  </span>
                )}

                {authenticated && (
                  <div
                    className="flex-1 items-end text-right text-[20px] cursor-pointer"
                    onClick={() => {
                      setExpandSmartTakes(!expandSmartTakes);
                      trackActivity(
                        expandSmartTakes
                          ? "smart_takes.section_collapsed"
                          : "smart_takes.section_expanded",
                        insight
                      );
                    }}
                  >
                    <FontAwesomeIcon
                      icon={expandSmartTakes ? faChevronUp : faChevronDown}
                    />
                  </div>
                )}
              </Title>
              {expandSmartTakes && <InsightSmartTakes insight={insight} />}
            </section>
          )}
        </ErrorBoundary>

        {loggedInAccount && <GoogleTranslationToogle type="alert-box" />}

        <ErrorBoundary autoRerender>
          <section className={style.insightSection}>
            <Title>
              <div
                className="inline cursor-pointer"
                onClick={() => toggleModal("sign-up")}
              >
                {authenticated ? t("EXECUTIVE SUMMARY") : t("SUMMARY")}
              </div>
              {shouldShowSignup && (
                <span
                  className="ml-[5px] font-normal text-ink-pink hover:text-ink-pink normal-case cursor-pointer"
                  onClick={() => toggleModal("sign-up")}
                >
                  ({t("Sign Up to Access")})
                </span>
              )}
              {loggedInAccount?.isPreviewPass && (
                <span className="ml-[5px] normal-case text-[14px]">
                  <FontAwesomeIcon icon={faUnlock} /> Unlocked
                </span>
              )}
            </Title>
            {shouldShowSignup && insight?.isPrimer && (
              <AlertBox className="mb-[15px]">
                <div className="">
                  Sign up for free to unlock the full Primer (no credit card
                  required)
                </div>
              </AlertBox>
            )}
            {shouldShowSignup && (
              <SignUpForm insightId={id} type="insight-signup" />
            )}
            <div
              ref={summaryRef}
              className={twMerge(!isPublic && "paywall-summary")}
            >
              {canViewDetail && insight.executiveSummary ? (
                <div className={style.summary_container}>
                  <RichContent html={insight.executiveSummary} />
                </div>
              ) : (
                <div className={style.summary_container}>
                  {(fromSearchBot ||
                    (summaryIntersection?.isIntersecting && authenticated)) && (
                    <Suspense fallback={`Loading...`}>
                      <InsightSummary slug={slug} />
                    </Suspense>
                  )}
                  {/* {canViewDetail ? null : <div className={style.fade_area} />} */}
                </div>
              )}
            </div>
          </section>
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          <section
            className={twMerge(
              style.insightSection,
              !isPublic && "paywall-detail"
            )}
            ref={insightDetailRef}
          >
            {canViewDetail ? (
              fromSearchBot ? (
                <InsightDetail slug={slug} />
              ) : restrictionReason ? (
                <div>
                  <Title className="flex cursor-pointer">
                    <div onClick={() => setExpandDetail(!expandDetail)}>
                      {t(
                        authenticated && !loggedInAccount?.isPreviewPass
                          ? "Detail"
                          : "Full Insight"
                      )}
                    </div>
                    {timeToRead && (
                      <span
                        className={twMerge(
                          "text-ink-pink normal-case ml-[5px]",
                          loggedInAccount?.isPreviewPass && "font-normal"
                        )}
                        onClick={() => setExpandDetail(!expandDetail)}
                      >
                        ({loggedInAccount?.isPreviewPass && "Paid Plans Only, "}
                        {timeToRead}-minute read)
                      </span>
                    )}
                    <div
                      className="flex-1 items-end text-right text-[20px] cursor-pointer"
                      onClick={() => setExpandDetail(!expandDetail)}
                    >
                      <FontAwesomeIcon
                        icon={expandDetail ? faChevronUp : faChevronDown}
                      />
                    </div>
                  </Title>
                  {loggedInAccount?.isPreviewPass ? (
                    <>
                      {expandDetail && (
                        <InsightRestrictionState insight={insight} />
                      )}
                    </>
                  ) : (
                    <InsightRestrictionState insight={insight} />
                  )}
                </div>
              ) : (
                insightDetailIntersection?.isIntersecting && (
                  <Suspense fallback={`Loading...`}>
                    <InsightDetail slug={slug} />
                  </Suspense>
                )
              )
            ) : (
              <>
                <Title className="flex cursor-pointer">
                  <div
                    className="inline-block"
                    onClick={() => setExpandDetail(!expandDetail)}
                  >
                    {t("Full Insight")}{" "}
                  </div>
                  <div
                    className="inline-block ml-[5px] font-normal text-ink-pink hover:text-ink-pink normal-case"
                    onClick={() => setExpandDetail(!expandDetail)}
                  >
                    ({t("Paid Plans Only")}
                    {timeToRead && `, ${timeToRead}-${t("minute read")}`})
                  </div>
                  <div
                    className="flex-1 items-end text-right text-[20px] cursor-pointer"
                    onClick={() => setExpandDetail(!expandDetail)}
                  >
                    <FontAwesomeIcon
                      icon={expandDetail ? faChevronUp : faChevronDown}
                    />
                  </div>
                </Title>
                {expandDetail && (
                  <div className="flex flex-col md:flex-row items-center px-[30px] md:pl-[60px] md:pr-[50px] py-[40px] text-[18px] font-medium">
                    <div className="w-full md:w-auto md:flex-1 mb-[10px] md:mb-0 md:mr-[40px] text-[#3A3A3C] leading-7 tracking-wide">
                      {t(
                        "Subscribe to read our full archive of Insights produced by our network of independent analysts. Select the right plan for you."
                      )}
                    </div>
                    <Link
                      href={`${emberAppBaseUrl}/home/pricing/`}
                      passHref
                      className={classNames(
                        "w-full md:w-[300px] h-[45px] py-[10px]",
                        "text-smart-teal text-center border-solid border-smart-teal rounded",
                        "hover:bg-smart-teal hover:text-white"
                      )}
                    >
                      {t("See all plans")}
                    </Link>
                  </div>
                )}
              </>
            )}
          </section>
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          {loggedInAccount?.isPreviewPass && isPublic && <AccountUpgradeForm />}
        </ErrorBoundary>

        <ErrorBoundary autoRerender>
          <div ref={attachmentRef}>
            {attachmentIntersection?.isIntersecting && (
              <Suspense fallback={`Loading...`}>
                <InsightDocuments id={insight.id} tagline={tagline} />
              </Suspense>
            )}
          </div>

          <DianomiAd type="in_list" contentClassName="!mx-0 !my-0" />

          {authenticated &&
            account?.id !== loggedInAccount?.id &&
            !restrictionReason && <InsightLikeSection insight={insight} />}
          {authenticated && !restrictionReason && (
            <InsightDisclosure insight={insight} />
          )}
          {(!authenticated ||
            !restrictionReason ||
            loggedInAccount?.isPreviewPass) && (
            <InsightRelatedContentV2 insight={insight} />
          )}

          {restrictionReason === "corporate" ? (
            <></>
          ) : !loggedInAccount?.isPreviewPass &&
            (restrictionReason ||
              (loggedInAccount && !loggedInAccount?.canViewComment)) ? (
            <section
              id="insight-discussions"
              className={style.insightSection}
              ref={discussionsRef}
            >
              <Title className="flex">
                <div className="inline-block">{t("Discussions")}</div>
              </Title>
              <div className="min-h-[725px] italic text-shady-gray text-[15px]">
                <DiscussionRestrictionState insight={insight} />
              </div>
            </section>
          ) : loggedInAccount?.canViewComment ? (
            <>
              {widgets}

              <section
                id="insight-discussions"
                className={style.insightSection}
                ref={discussionsRef}
              >
                <Title className="flex">
                  <div className="inline-block">{t("Discussions")}</div>
                </Title>
                <InsightDiscussionsList key={slug} slug={slug} />
              </section>
            </>
          ) : (
            <>
              {!authenticated && isPublic && !insight?.isPrimer && (
                <div className="mt-[30px]">
                  <SignUpForm insightId={id} type="insight-signup" />
                </div>
              )}

              <section
                id="insight-discussions"
                className={style.insightSection}
                ref={discussionsRef}
              >
                <Title className="flex">
                  <div className="inline-block">{t("Discussions")}</div>
                  <div className="inline-block ml-[5px] font-normal text-ink-pink hover:text-ink-pink normal-case">
                    ({t("Paid Plans Only")})
                  </div>
                  <div
                    className="flex-1 items-end text-right text-[20px] cursor-pointer"
                    onClick={() => setExpandDiscussions(!expandDiscussions)}
                  >
                    <FontAwesomeIcon
                      icon={expandDiscussions ? faChevronUp : faChevronDown}
                    />
                  </div>
                </Title>
                {discussionsIntersection?.isIntersecting &&
                  expandDiscussions && (
                    <div className="flex flex-col md:flex-row items-center px-[30px] md:pl-[60px] md:pr-[50px] py-[40px] text-[18px] font-medium">
                      <div className="w-full md:w-auto md:flex-1 mb-[10px] md:mb-0 md:mr-[40px] text-[#3A3A3C] leading-7 tracking-wide">
                        {t(
                          "Your subscription enables you to engage in discussions with our network of independent analysts, investor relations professionals, and investors."
                        )}
                      </div>
                      <Link
                        href={`${emberAppBaseUrl}/home/pricing/`}
                        className={classNames(
                          "w-full md:w-[300px] h-[45px] py-[10px]",
                          "text-smart-teal text-center border-solid border-smart-teal rounded",
                          "hover:bg-smart-teal hover:text-white"
                        )}
                        onClick={(e) => {
                          if (loggedInAccount?.isPreviewPass) {
                            e.preventDefault();
                            setExpandDetail(true);
                          }
                        }}
                      >
                        {t("See all plans")}
                      </Link>
                    </div>
                  )}
              </section>

              {widgets}
            </>
          )}
        </ErrorBoundary>
      </div>
    </ErrorBoundary>
  );
};

export default InsightLongForm;
